import { Box, Tab, Tabs, useMediaQuery, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import React from 'react';
import { FaClipboardList, FaEnvelopeOpenText, FaLayerGroup, FaUsers, FaUserShield } from 'react-icons/fa';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

// Define the paths for the tabs
const tabPaths = ['groups', 'services', 'members', 'roles', 'contact-submissions'];

const PlatformTabs: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();

  // Determine the active tab based on the URL
  const tabIndex = tabPaths.indexOf(location.pathname.split('/').pop() || 'groups');
  const currentTab = tabIndex >= 0 ? tabIndex : 0;

  // Handle tab change and update the URL
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    navigate(`/platform-admin/${tabPaths[newValue]}`);
  };

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      {/* Tabs Section */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: theme.palette.background.default,
          borderBottom: 1,
          borderColor: 'divider',
          px: isMobile ? 0 : 1
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? true : 'auto'}
          allowScrollButtonsMobile
          aria-label="platform admin tabs"
          sx={{
            mb: isMobile ? 1 : 3,
            minHeight: isMobile ? 56 : 64, // Slightly reduced height on mobile
            '.MuiTab-root': {
              fontWeight: 600,
              textTransform: 'none',
              display: 'flex',
              flexDirection: 'column', // Stack icon and label vertically on mobile
              alignItems: 'center',
              justifyContent: 'center',
              gap: isMobile ? 0.25 : 1,
              minHeight: isMobile ? 56 : 64,
              fontSize: isMobile ? '0.7rem' : '0.875rem', // Smaller font on mobile
              px: isMobile ? 0.75 : 2,
              '&.Mui-selected': {
                color: theme.palette.primary.main
              }
            },
            '.MuiTabs-indicator': {
              height: isMobile ? 2 : 3
            }
          }}
        >
          <Tab
            label="Groups" // Always show label
            icon={<FaLayerGroup size={isMobile ? 18 : 20} />} // Slightly smaller icon on mobile
            iconPosition={isMobile ? 'top' : 'start'}
            sx={{
              minWidth: isMobile ? 80 : 'auto', // Compact but wide enough for text
              py: isMobile ? 0.5 : 1.5
            }}
          />
          <Tab
            label="Services"
            icon={<FaClipboardList size={isMobile ? 18 : 20} />}
            iconPosition={isMobile ? 'top' : 'start'}
            sx={{
              minWidth: isMobile ? 80 : 'auto',
              py: isMobile ? 0.5 : 1.5
            }}
          />
          <Tab
            label="Members"
            icon={<FaUsers size={isMobile ? 18 : 20} />}
            iconPosition={isMobile ? 'top' : 'start'}
            sx={{
              minWidth: isMobile ? 80 : 'auto',
              py: isMobile ? 0.5 : 1.5
            }}
          />
          <Tab
            label="Roles"
            icon={<FaUserShield size={isMobile ? 18 : 20} />}
            iconPosition={isMobile ? 'top' : 'start'}
            sx={{
              minWidth: isMobile ? 80 : 'auto',
              py: isMobile ? 0.5 : 1.5
            }}
          />
          <Tab
            label="Contact"
            icon={<FaEnvelopeOpenText size={isMobile ? 18 : 20} />}
            iconPosition={isMobile ? 'top' : 'start'}
            sx={{
              minWidth: isMobile ? 90 : 'auto',
              py: isMobile ? 0.5 : 1.5
            }}
          />
        </Tabs>
      </Box>

      {/* Tab Content Section */}
      <Box
        sx={{
          p: isMobile ? 1 : 3,
          borderRadius: isMobile ? 0 : 2,
          bgcolor: 'background.default',
          boxShadow: isMobile ? 0 : 1,
          overflowY: 'auto',
          maxHeight: isMobile ? 'calc(100vh - 136px)' : 'calc(100vh - 200px)', // Adjusted for taller tabs
          minHeight: isMobile ? 'calc(100vh - 136px)' : undefined
        }}
      >
        <motion.div
          key={currentTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: isMobile ? 0.2 : 0.3 }}
        >
          <Outlet />
        </motion.div>
      </Box>
    </Box>
  );
};

export default PlatformTabs;
